import { Search } from './search-services';
import panelUpdater from '@remarkablenet/panel-updater';
import * as CSSTH from '../css-transition-helper/css-transition-helper';
import {
    debounce,
    disableScroll,
    dispatchCustomEventWrapper,
    enableScroll
} from '../utility-functions/utility-functions';
import { closeOffCanvasPanel, createBackdrop, handleCloseEvent } from '../off-canvas-panel/off-canvas-panel';
import { dispatchEventWrapper, gaTracking } from '../event/event';
import * as slideoutMenu from '../slideout-menu/slideout-menu';
import { isMobile, isTablet } from '../mobile-detect/mobile-detect';
import addDelegateEventListener from '../utility-functions/add-delegate-event-listener';

export const DEFAULTS = {
    toggleSearch: '.js-toggle-search',
    searchContainer: '.search-container',
    createBackDrop: false,
    backdropType: 'real', // 'fake' or 'real'
    searchInput: '.js-search-input',
    searchInputClear: '.js-search-input-clear',
    searchResults: '.js-search-results',
    searchContainerClose: '.js-search-container-close',
    searchForm: '.search-form',
    suggestedSearch: '.js-suggested-search'
};

const SEARCH_RESULTS_SELECTOR = `${DEFAULTS.searchContainer} ${DEFAULTS.searchResults}`;

export const init = () => {
    addSearchContainerEventListeners();
    addSearchFormListeners(document.querySelectorAll(DEFAULTS.searchForm));
};

export const updateUI = (resultElm: HTMLElement, content) => (resultElm.innerHTML = content);

export const clearSearchInput = (searchContainer: HTMLElement) => {
    const searchInput = searchContainer.querySelector(DEFAULTS.searchInput) as HTMLInputElement;
    searchInput.value = '';
};
export const getDefaultState = () => {
    const resultElmList: NodeListOf<HTMLElement> = document.querySelectorAll(SEARCH_RESULTS_SELECTOR);
    Search('').then((response) => {
        resultElmList.forEach((resultElm) => updateUI(resultElm, response));
    });
};

export const openSearchPanel = (target: HTMLElement) => {
    checkToCloseMenu();
    if (DEFAULTS.createBackDrop) {
        if (DEFAULTS.backdropType === 'fake') document.body.classList.add('search-fake-backdrop');
        createBackdrop().then((backdrop) => {
            CSSTH.activate(backdrop);
            backdrop.addEventListener('click', () => {
                handleCloseEvent(false);
                searchCloseEvents();
            });
        });
    }
    isMobile() && isTablet() && disableScroll();
    if (isMobile() || isTablet()) target.classList.remove('d-none');
    showResults();
    return CSSTH.activate(target);
};

const closeSearchPanel = (target: HTMLElement) => {
    isMobile() && isTablet() && enableScroll();
    enableScroll();
    closeOffCanvasPanel(target);
    CSSTH.deactivate(target);    
};

const performSearch: Function = (QUERY: string, searchForm: HTMLFormElement) => {
    const resultsPanel = searchForm
        .closest(DEFAULTS.searchContainer)
        .querySelector(DEFAULTS.searchResults) as HTMLElement;
    panelUpdater(resultsPanel, Search(QUERY)).then((response) => {
        updateUI(resultsPanel, response);
        dispatchEventWrapper(document, 'rc-quick-search', {
            area: 'Search',
            event: 'Quick Search',
            value: QUERY
        });
    });
};

const suggestedSearch = (suggestedSearchItem: HTMLElement, form: HTMLFormElement) => {
    const searchInput: HTMLInputElement = document.querySelector(DEFAULTS.searchInput) as HTMLInputElement;
    const suggestedTerm: string = suggestedSearchItem.getAttribute('data-searchterm');
    searchInput.value = suggestedTerm;
    performSearch(suggestedTerm, form);
};

const addSearchContainerEventListeners = () => {
    const triggerElements = document.querySelectorAll(DEFAULTS.toggleSearch);
    const closeTriggers = document.querySelectorAll(DEFAULTS.searchContainerClose);

    triggerElements.forEach((triggerElement) => {
        triggerElement.addEventListener('click', (e) => {
            e.preventDefault();
            checkToCloseMenu();
            const searchContainer = document.querySelector(DEFAULTS.searchContainer) as HTMLElement;
            openSearchPanel(searchContainer).then(() => {
                const searchInput = searchContainer.querySelector(DEFAULTS.searchInput) as HTMLInputElement;
                searchInput.focus();
                iOSClickFocus(searchInput);
            });
        });
    });
    closeTriggers.forEach((closeTrigger) => {
        closeTrigger.addEventListener('click', (e) => {
            e.preventDefault();
            searchCloseEvents();
        });
    });
    document.addEventListener('click', function (e) {
        if (!(e.target as HTMLElement).closest('.search-container')) {
            resetResults();
            clearSearchInput(document.querySelector(DEFAULTS.searchContainer) as HTMLElement);
        }
    });
};

function iOSClickFocus(targetInput) {
    // create invisible dummy input to receive the focus first
    const fakeInput = document.createElement('input');
    fakeInput.setAttribute('type', 'text');
    fakeInput.setAttribute('autofocus', 'true');
    fakeInput.style.position = 'absolute';
    fakeInput.style.opacity = `0`;
    fakeInput.style.height = `0`;
    fakeInput.style.fontSize = '16px'; // disable auto zoom
    // you may need to append to another element depending on the browser's auto
    // zoom/scroll behavior
    document.body.prepend(fakeInput);
    // focus so that subsequent async focus will work
    fakeInput.focus();
    setTimeout(() => {
        // now we can focus on the target input
        targetInput.focus();
        // cleanup
        fakeInput.remove();
    }, 1000);
}

const searchCloseEvents = () => {
    const searchContainer = document.querySelector(DEFAULTS.searchContainer) as HTMLElement;
    closeSearchPanel(searchContainer);
    getDefaultState();
    clearSearchInput(searchContainer);
    searchContainer.classList.add('d-none');
};

const addSearchFormListeners = (searchForms: NodeListOf<HTMLFormElement>) => {
    searchForms.forEach((searchForm) => {
        const searchInput = searchForm.querySelector(DEFAULTS.searchInput) as HTMLInputElement;
        searchInput.addEventListener(
            'keyup',
            debounce((e) => {
                e.preventDefault();
                const QUERY = searchInput.value;
                const encodedQuery = encodeURIComponent(QUERY);
                if (QUERY == "") resetResults();
                else showResults();
                performSearch(encodedQuery, searchForm);
                setTimeout(() => {
                    closeDSearch();
                }, 1000);
            }, 350)
        );
        searchInput.addEventListener(
            'paste',
            debounce((e) => {
                e.preventDefault();
                const QUERY = searchInput.value;
                const encodedQuery = encodeURIComponent(QUERY);
                if (QUERY == "") resetResults();
                else showResults();
                performSearch(encodedQuery, searchForm);
                setTimeout(() => {
                    closeDSearch();
                }, 1000);
            }, 350)
        );
        searchForm.addEventListener('reset', (e) => {
            e.preventDefault();
            getDefaultState();
            clearSearchInput(searchForm.closest(DEFAULTS.searchContainer) as HTMLElement);
            resetResults();
        });
        searchForm.addEventListener('submit', (e) => {
            e.preventDefault();
            const QUERY = searchInput.value;
            if (QUERY == "") alert('Please enter a search term.');
            else searchForm.submit();
            gaTracking("search", false, {
                search_term: QUERY
            });
        });

        addDelegateEventListener(searchForm, 'click', DEFAULTS.suggestedSearch, (e) => {
            e.originalEvent.preventDefault();
            suggestedSearch(e.delegateTarget, searchForm);
        });
    });
};

const checkToCloseMenu = () => {
    if (!document.querySelector('html').classList.contains('slideout-open')) return;
    dispatchCustomEventWrapper(document, 'rc-close-menu-fn', {});
};

const showResults = () => {
    const resultsContainer = document.querySelector(DEFAULTS.searchResults) as HTMLInputElement;
    resultsContainer?.classList.remove('d-none');
};

const resetResults = () => {
    const resultsContainer = document.querySelector(DEFAULTS.searchResults) as HTMLInputElement;
    resultsContainer?.classList.add('d-none');
    resultsContainer.innerHTML = "";
};

export const closeDSearch = () => {
    const closeBtn = document.querySelector('.js-close-d-search') as HTMLElement;
    closeBtn?.addEventListener('click', e => {
        e.preventDefault();
        resetResults();
        clearSearchInput(document.querySelector(DEFAULTS.searchContainer) as HTMLElement);
    });
}